import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getMessaging, isSupported } from 'firebase/messaging';
import { getFunctions as getFirebaseFunctions, httpsCallable as firebaseHttpsCallable } from 'firebase/functions';
import { firebaseConfig, FUNCTIONS_REGION, COLLECTIONS, STORAGE_PATHS, PAGINATION_DEFAULTS, CACHE_DURATION } from './firebaseConfig';

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Initialize messaging only if supported
// Check for IndexedDB support as it's required by Firebase Messaging
const checkIndexedDBSupport = (): boolean => {
  try {
    if (typeof window === 'undefined') return false;
    if (!('indexedDB' in window)) return false;
    if (!window.indexedDB) return false;
    
    // Basic IndexedDB availability check - be more permissive
    // The actual functionality will be tested when Firebase tries to use it
    return true;
  } catch {
    return false;
  }
};

// Initialize messaging with comprehensive browser support checks
export const messaging = (() => {
  // Early return for non-browser environments
  if (typeof window === 'undefined') {
    return null;
  }
  
  // Check if browser supports messaging APIs - fail fast
  if (!('serviceWorker' in navigator)) {
    return null;
  }
  
  if (!('PushManager' in window)) {
    return null;
  }
  
  if (!('Notification' in window)) {
    return null;
  }
  
  // Check IndexedDB support (required by Firebase Messaging)
  if (!checkIndexedDBSupport()) {
    return null;
  }
  
  // Check if protocol is HTTPS or localhost
  if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
    return null;
  }
  
  // Only attempt initialization if all checks pass
  try {
    const messagingInstance = getMessaging(app);
    return messagingInstance;
  } catch (error) {
    return null;
  }
})();

export const functions = getFirebaseFunctions(app, FUNCTIONS_REGION);

// Backward compatibility exports
export const getFunctions = () => functions;
export const httpsCallable = (instance: any, functionName: string) => {
  return firebaseHttpsCallable(instance, functionName);
};

// Re-export constants
export { COLLECTIONS, STORAGE_PATHS, PAGINATION_DEFAULTS, CACHE_DURATION };

export default app;